import React, { useEffect, useMemo, useState, useTransition } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { login, logout } from '../../services/auth.service';
import './AppHeader.scss';
import { Logo } from '../logo/icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { atom } from 'jotai';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDesktopMode } from '../../services/hooks/useDesktopMode';
import Container from '@mui/material/Container/Container';
import Stack from '@mui/material/Stack/Stack';
import Button from '@mui/material/Button/Button';
import CircularProgress from '@mui/material/CircularProgress/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress/LinearProgress';
import IconButton from '@mui/material/IconButton/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Drawer from '@mui/material/Drawer/Drawer';
import List from '@mui/material/List/List';
import ListItem from '@mui/material/ListItem/ListItem';
import ListItemButton from '@mui/material/ListItemButton/ListItemButton';
import ListSubheader from '@mui/material/ListSubheader/ListSubheader';
import ListItemText from '@mui/material/ListItemText/ListItemText';
import Divider from '@mui/material/Divider/Divider';
import Badge from '@mui/material/Badge/Badge';
import Avatar from '@mui/material/Avatar/Avatar';
import {
    HomeIcon,
    MentorshipIcon,
    TeamsIcon,
    ProfileIcon,
    EditIcon,
    MembersIcon,
    ManagerDashboardIcon,
    CompetencyIcon,
    AssessmentIcon,
    SearchIcon,
    BadgeIcon,
    QuestsIcon,
    NotificationsIcon,
    GoalsIcon,
    OrgChartIcon,
    ApiKeyIcon,
    ApiDocsIcon,
    SettingsIcon,
} from '../../constants/icons';
import { NotificationSidebar } from '../notifications/NotificationSidebar';
import { useUnreadNotificationCount } from '../../hooks/useNotifications';

import { CrewDTO, Profile } from '../../../../shared/types';
import { useHasDirectReports } from '../../hooks/useHasDirectReports';
import { GlobalSearchDropdown } from '../common/GlobalSearchDropdown';
import { useProfileQuests } from '../../hooks/useProfileQuests';
import ProfileQuestDrawer from '../quests/ProfileQuestDrawer';
import Box from '@mui/material/Box/Box';
import Typography from '@mui/material/Typography/Typography';
import { useCurrentCrewMember } from '../../hooks/useCurrentCrewMember';
import { useCrewModuleAccess } from '../../hooks/useCrews';


export const NavMenuOpened = atom(false);


export default function AppHeader({ crew, profile }: { crew: CrewDTO, profile: Profile }) {
    const desktopMode = useDesktopMode();

    const { user, loading } = useAuth();

    const { hasDirectReports } = useHasDirectReports(crew.slug, profile.handle);

    const moduleAccess = useCrewModuleAccess(crew.slug);

    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const openProfileMenu = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const navigate = useNavigate();

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [questDrawerOpen, setQuestDrawerOpen] = useState(false);
    const [notifSidebarOpen, setNotifSidebarOpen] = useState(false);
    const [isPending, startTransition] = useTransition();

    const { data: unreadNotifCount = 0 } = useUnreadNotificationCount(crew.slug);

    const location = useLocation();

    const closeAndNavigate = (path: string) => {
        setDrawerOpen(false);
        startTransition(() => {
            navigate(path);
        });
    };

    const { questGroups, totals, updateQuestStatus, setGroupSkippedOrCompleted } = useProfileQuests({
        profile,
        hasDirectReports
    });

    const questBadgeCount = useMemo(() => {
        return totals.remainingCount;
    }, [totals.remainingCount]);

    useEffect(() => {
        return () => {
            // Reset navigation state on unmount to prevent stale state when navigating between crews
            setDrawerOpen(false);
            setQuestDrawerOpen(false);
            setNotifSidebarOpen(false);
        }
    }, [])

    return (
        <Box className="app-header no-print-no-display" id="back-to-top-anchor" sx={{
            color: 'text.primary',
            backgroundColor: 'background.default',
            position: 'relative'
        }} >

            <Container >
                <Stack direction="row" alignItems="center" spacing={2} className="header-container">
                    <IconButton title={'Menu'} onClick={() => setDrawerOpen(true)}><MenuIcon /></IconButton>

                    <Link to={'/'} className='main-link'>
                        <Logo size="small" />
                        {desktopMode && <Typography component="h1" variant="h6" color="text.primary">Wayfinder</Typography>}
                    </Link>
                    {desktopMode && (
                        <GlobalSearchDropdown
                            width={360}
                            placeholder="Search people, teams, badges, competencies..."
                            onSearchNavigate={() => updateQuestStatus('memberOnboarding.usedSearch')}
                        />
                    )}
                    <div className="spacer"></div>
                    {desktopMode && (
                        <IconButton
                            title="Quests"
                            color="primary"
                            onClick={() => setQuestDrawerOpen(true)}
                        >
                            <Badge
                                badgeContent={questBadgeCount}
                                color="secondary"
                                overlap="circular"
                                showZero={false}
                            >
                                <QuestsIcon />
                            </Badge>
                        </IconButton>
                    )}
                    <IconButton
                        title="Notifications"
                        color="primary"
                        onClick={() => setNotifSidebarOpen(true)}
                    >
                        <Badge
                            badgeContent={unreadNotifCount}
                            color="error"
                            overlap="circular"
                            showZero={false}
                        >
                            <NotificationsIcon />
                        </Badge>
                    </IconButton>
                    {!desktopMode && (
                        <IconButton
                            title="Search"
                            color="primary"
                            onClick={() => {
                                updateQuestStatus('memberOnboarding.usedSearch');
                                navigate(`/c/${crew.slug}/search`);
                            }}
                        >
                            <SearchIcon />
                        </IconButton>
                    )}
                    {
                        loading && <CircularProgress />
                    }
                    {
                        !loading && !user && <Button onClick={(e) => { login(); }}>Login/Register</Button>
                    }
                    {
                        (!loading && !!user) && <>
                            {desktopMode ? (
                                <Button
                                    id="header-user-menu-button"
                                    aria-controls={openProfileMenu ? 'header-user-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={openProfileMenu ? 'true' : undefined}
                                    onClick={handleClick}
                                    startIcon={
                                        <Avatar
                                            src={profile.profilePicture}
                                            sx={{ width: 24, height: 24, fontSize: 12 }}
                                        >
                                            {profile.displayName?.charAt(0).toUpperCase()}
                                        </Avatar>
                                    }
                                >
                                    {profile.displayName}
                                </Button>
                            ) : (
                                <IconButton
                                    id="header-user-menu-button"
                                    aria-controls={openProfileMenu ? 'header-user-menu' : undefined}
                                    aria-haspopup="true"
                                    aria-expanded={openProfileMenu ? 'true' : undefined}
                                    onClick={handleClick}
                                    size="small"
                                >
                                    <Avatar
                                        src={profile.profilePicture}
                                        sx={{ width: 28, height: 28, fontSize: 13 }}
                                    >
                                        {profile.displayName?.charAt(0).toUpperCase()}
                                    </Avatar>
                                </IconButton>
                            )}
                            <Menu
                                id="header-user-menu"
                                anchorEl={anchorEl}
                                open={openProfileMenu}
                                onClose={handleClose}
                                MenuListProps={{
                                    'aria-labelledby': 'header-user-menu-button'
                                }}
                            >
                                <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Avatar
                                        src={profile.profilePicture}
                                        sx={{ width: 36, height: 36 }}
                                    >
                                        {profile.displayName?.charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="subtitle2" fontWeight={600}>{profile.displayName}</Typography>
                                        <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                                    </Box>
                                </Box>
                                <Divider />
                                <MenuItem onClick={() => { handleClose(); setTimeout(() => navigate(`/c/${crew.slug}/profile/${profile.handle}`)); }}>
                                    <ProfileIcon sx={{ mr: 1.5, fontSize: 18 }} /> My Profile
                                </MenuItem>
                                <MenuItem onClick={() => { handleClose(); setTimeout(() => navigate(`/c/${crew.slug}/profile/${profile.handle}/edit`)); }}>
                                    <EditIcon sx={{ mr: 1.5, fontSize: 18 }} /> Edit Profile
                                </MenuItem>
                                <MenuItem onClick={() => { handleClose(); setTimeout(() => navigate(`/c/${crew.slug}/api-keys`)); }}>
                                    <ApiKeyIcon sx={{ mr: 1.5, fontSize: 18 }} /> API Keys
                                </MenuItem>
                                <MenuItem onClick={() => { handleClose(); setTimeout(() => navigate(`/c/${crew.slug}/api-docs`)); }}>
                                    <ApiDocsIcon sx={{ mr: 1.5, fontSize: 18 }} /> API Docs
                                </MenuItem>
                                <Divider />
                                <MenuItem onClick={() => { handleClose(); setTimeout(() => navigate('/')) }}>Switch Crew</MenuItem>
                                <Divider />
                                <MenuItem onClick={logout}>Logout</MenuItem>
                            </Menu>
                        </>
                    }
                </Stack>
            </Container>
            {isPending && (
                <LinearProgress sx={{ position: 'absolute', bottom: -4, left: 0, right: 0, height: 4 }} />
            )}

            {/* Hamburger Nav Menu */}
            <Drawer open={drawerOpen} onClose={() => setDrawerOpen(false)}>
                <List sx={{ width: 250 }}>
                    {/* Discover Section */}
                    <ListSubheader>Discover</ListSubheader>

                    <ListItem key="crew-home" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}`)}
                        >
                            <HomeIcon sx={{ mr: 2 }} />
                            <ListItemText primary="Home" />
                        </ListItemButton>
                    </ListItem>

                    <ListItem key="members" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}/members`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}/members`)}
                        >
                            <MembersIcon sx={{ mr: 2 }} />
                            <ListItemText primary="Members" />
                        </ListItemButton>
                    </ListItem>

                    <ListItem key="teams" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}/teams`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}/teams`)}
                        >
                            <TeamsIcon sx={{ mr: 2 }} />
                            <ListItemText primary="Teams" />
                        </ListItemButton>
                    </ListItem>

                    {moduleAccess.goals && (
                        <ListItem key="goals" disablePadding>
                            <ListItemButton
                                selected={location.pathname === `/c/${crew.slug}/goals`}
                                onClick={() => closeAndNavigate(`/c/${crew.slug}/goals`)}
                            >
                                <GoalsIcon sx={{ mr: 2 }} />
                                <ListItemText primary="Goals" />
                            </ListItemButton>
                        </ListItem>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Growth & Recognition Section */}
                    <ListSubheader>Growth & Recognition</ListSubheader>

                    {moduleAccess.competencyDefinitions && (
                        <ListItem key="my-competencies" disablePadding>
                            <ListItemButton
                                selected={location.pathname === `/c/${crew.slug}/competencies`}
                                onClick={() => closeAndNavigate(`/c/${crew.slug}/competencies`)}
                            >
                                <CompetencyIcon sx={{ mr: 2 }} />
                                <ListItemText primary="My Competencies" />
                            </ListItemButton>
                        </ListItem>
                    )}

                    {moduleAccess.mentorship && (
                        <ListItem key="mentorship" disablePadding>
                            <ListItemButton
                                selected={location.pathname === `/c/${crew.slug}/mentorship`}
                                onClick={() => closeAndNavigate(`/c/${crew.slug}/mentorship`)}
                            >
                                <MentorshipIcon sx={{ mr: 2 }} />
                                <ListItemText primary="Mentorship" />
                            </ListItemButton>
                        </ListItem>
                    )}

                    {moduleAccess.badges && (
                        <ListItem key="badges" disablePadding>
                            <ListItemButton
                                selected={location.pathname === `/c/${crew.slug}/badges`}
                                onClick={() => closeAndNavigate(`/c/${crew.slug}/badges`)}
                            >
                                <BadgeIcon sx={{ mr: 2 }} />
                                <ListItemText primary="Badges" />
                            </ListItemButton>
                        </ListItem>
                    )}

                    {/* Management Section - manager tools; require org chart (direct reports).
                        Competency content inside soft-empties when definitions are off. */}
                    {hasDirectReports && moduleAccess.orgChart && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <ListSubheader>Management</ListSubheader>
                            <ListItem key="manager-dashboard" disablePadding>
                                <ListItemButton
                                    selected={location.pathname === `/c/${crew.slug}/competencies/dashboard`}
                                    onClick={() => closeAndNavigate(`/c/${crew.slug}/competencies/dashboard`)}
                                >
                                    <ManagerDashboardIcon sx={{ mr: 2 }} />
                                    <ListItemText primary="Manager Dashboard" />
                                </ListItemButton>
                            </ListItem>
                            <ListItem key="assess-team" disablePadding>
                                <ListItemButton
                                    selected={location.pathname === `/c/${crew.slug}/competencies/assess`}
                                    onClick={() => closeAndNavigate(`/c/${crew.slug}/competencies/assess`)}
                                >
                                    <AssessmentIcon sx={{ mr: 2 }} />
                                    <ListItemText primary="Team Assessments" />
                                </ListItemButton>
                            </ListItem>
                        </>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Account Section */}
                    <ListSubheader>Account</ListSubheader>

                    <ListItem key="profile" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}/profile/${profile.handle}`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}/profile/${profile.handle}`)}
                        >
                            <ProfileIcon sx={{ mr: 2 }} />
                            <ListItemText primary="My Profile" />
                        </ListItemButton>
                    </ListItem>

                    <ListItem key="api-keys" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}/api-keys`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}/api-keys`)}
                        >
                            <ApiKeyIcon sx={{ mr: 2 }} />
                            <ListItemText primary="API Keys" />
                        </ListItemButton>
                    </ListItem>

                    <ListItem key="api-docs" disablePadding>
                        <ListItemButton
                            selected={location.pathname === `/c/${crew.slug}/api-docs`}
                            onClick={() => closeAndNavigate(`/c/${crew.slug}/api-docs`)}
                        >
                            <ApiDocsIcon sx={{ mr: 2 }} />
                            <ListItemText primary="API Docs" />
                        </ListItemButton>
                    </ListItem>

                    {(profile.isCrewAdmin || profile.isPeopleAdmin) && (
                        <>
                            <Divider sx={{ my: 1 }} />
                            <ListSubheader>Admin</ListSubheader>
                            {(profile.isCrewAdmin || profile.isPerseraAdmin) && (
                                <ListItem key="crew-settings" disablePadding>
                                    <ListItemButton
                                        selected={location.pathname === `/c/${crew.slug}/settings`}
                                        onClick={() => closeAndNavigate(`/c/${crew.slug}/settings`)}
                                    >
                                        <SettingsIcon sx={{ mr: 2 }} />
                                        <ListItemText primary="Crew Settings" />
                                    </ListItemButton>
                                </ListItem>
                            )}
                            {moduleAccess.orgChart && (
                                <ListItem key="org-structure" disablePadding>
                                    <ListItemButton
                                        selected={location.pathname === `/c/${crew.slug}/org-structure`}
                                        onClick={() => closeAndNavigate(`/c/${crew.slug}/org-structure`)}
                                    >
                                        <OrgChartIcon sx={{ mr: 2 }} />
                                        <ListItemText primary="Org Structure" />
                                    </ListItemButton>
                                </ListItem>
                            )}
                        </>
                    )}
                </List>
            </Drawer>
            <ProfileQuestDrawer
                open={questDrawerOpen}
                onClose={() => setQuestDrawerOpen(false)}
                questGroups={questGroups}
                totals={totals}
                setGroupSkippedOrCompleted={setGroupSkippedOrCompleted}
            />
            <NotificationSidebar
                open={notifSidebarOpen}
                onClose={() => setNotifSidebarOpen(false)}
                crewSlug={crew.slug}
            />
        </Box>
    );
}
